// Styled components for the v2 Due Diligence report (MIKE-322 design).
//
// Shared by user-interface (the client's report) and admin-interface (the
// staff editor that renders the very same markup). The two apps declare
// incompatible styled-components themes, so the design tokens this file used
// to read off `props.theme` are inlined as literals — the same values the DD
// theme carries, just not routed through a `DefaultTheme` neither app agrees on.
import styled, { keyframes } from "styled-components";
import {
  TINT_NEUTRAL,
  BLACK,
  CHROME_INK,
  TEXT_PLACEHOLDER,
  AMBER_INK,
  UNDERLINE_GREEN,
  WHITE,
  TEXT_LEAD,
  TINT_AMBER,
  TINT_RED,
  TINT_RED_PINK,
  BORDER_TINT,
  TINT_GREEN,
  WASH,
  GREEN_ACCENT,
  BORDER_GREEN,
  INK,
  BORDER_SUBTLE,
  BRAND_GREEN,
  BRAND_NAVY,
  CARD_BORDER,
  FONT_FIGTREE,
  FONT_REPORT,
  SEMAPHORE,
  SEMAPHORE_TONES,
  TONE,
  SEMAPHORE_WITH_NEUTRAL,
  SURFACE_CARD,
  SURFACE_GREY,
  SURFACE_GREY_DEEP,
  BORDER_DASHED,
  BORDER_GREY,
  TEXT_BODY,
  TEXT_HEADING,
  TEXT_META,
  TEXT_VALUE,
} from "../tokens";

// ── Semantic status colors ───────────────────────────────────────
// The report's badge palette: a Tailwind-ish red/amber/emerald/slate family,
// separate from the design's own semaphore (see tokens.ts) and spelled the
// way the payload spells it — its fourth state is "grey", not "neutral".
//
// `dot` is the solid mark. It is not `fg`: at amber and grey the design
// prints a brighter dot than the word next to it, which is why the dot
// colors kept being re-typed by hand next to every pill that has one.
const STATUS_COLORS = {
  green: { bg: "#e6f9f0", fg: "#237A50", dot: "#35A46E" },
  amber: { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  red: { bg: "#fef2f2", fg: "#dc2626", dot: "#dc2626" },
  grey: { bg: "#f3f4f6", fg: "#6b7280", dot: "#9ca3af" },
} as const;

// ── Subheader (sticky top bar) ───────────────────────────────────
// Callers should render this with `data-subheader` so the page's
// scrollToSection helper can measure its real bottom at scroll time and
// land section-chip anchors flush with the "Sección X" green label
// regardless of viewport / breakpoint.
export const SubheaderBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${WHITE};
  border-bottom: 1px solid ${BORDER_GREY};
  padding: 0.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    top: 4rem;
  }
`;

export const SubheaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

export const SubheaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  color: ${TEXT_META};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.35rem 0.5rem;
  border-radius: 0.375rem;
  white-space: nowrap;

  &:hover {
    background: ${SURFACE_GREY};
    color: ${TEXT_HEADING};
  }
`;

export const CurrentSectionIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${TEXT_HEADING};
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SectionNumberBadge = styled.span`
  background: ${BRAND_NAVY};
  color: ${WHITE};
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
`;

export const SectionNameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SubheaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: #111827;
  color: ${WHITE};
  border: none;
  font-size: 0.75rem;
  font-weight: 600;
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  /* Only collapse to icon-only on truly small (mobile) screens — at ~600px
   * the "Generando informe" / "Exportar PDF" label still fits comfortably. */
  @media (max-width: 600px) {
    padding: 0 0.4rem;

    span {
      display: none;
    }
  }
`;

export const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${CARD_BORDER};
  color: ${TEXT_META};
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  cursor: pointer;

  &:hover {
    background: ${SURFACE_GREY};
    color: ${TEXT_HEADING};
  }
`;

export const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: ${BRAND_GREEN};
  transition: width 0.3s ease;
`;

// ── Page layout ──────────────────────────────────────────────────
export const ReportPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  font-family: ${FONT_REPORT};
  font-size: 14px;
  line-height: 1.625;
  color: ${TEXT_BODY};
  overflow-x: clip;
  margin-top: 0;
  margin-left: calc(-1 * 2rem);
  margin-right: calc(-1 * 2rem);
  width: calc(100% + 2rem * 2);

  @media (max-width: 768px) {
    margin-top: 0;
    margin-left: calc(-1 * 1rem);
    margin-right: calc(-1 * 1rem);
    width: calc(100% + 1rem * 2);
  }
`;

export const ReportWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const ReportHeader = styled.div`
  margin-bottom: 0;
`;

export const ReportTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin: 0 0 0.5rem 0;
`;

export const ReportType = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${TEXT_META};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
`;

export const TitleSeparator = styled.span`
  color: rgba(0, 0, 0, 0.12);
  font-weight: 300;
  align-self: center;
  user-select: none;
`;

export const ReportAddress = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: ${TEXT_HEADING};
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.025em;
`;

export const ReportMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  color: ${TEXT_META};
  flex-wrap: wrap;
`;

export const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: ${SURFACE_GREY};
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.4;
`;

export const MetaPillSeparator = styled.span`
  color: rgba(0, 0, 0, 0.18);
  margin: 0 0.1rem;
`;

export const HeaderDivider = styled.hr`
  border: none;
  border-top: 1px solid ${CARD_BORDER};
  margin: 1rem 0;
`;

// ── Cockpit header (final report, paid) ─────────────────────────────
// Replaces ReportHeader's simple title row with the "z-veredicto" layout:
// a category badge + big title + address/cadastral line on the left, and a
// small report-type/ref/date column on the right.
export const CockpitHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
`;

export const CockpitHeaderLeft = styled.div`
  flex: 1 1 360px;
  min-width: 0;
`;

export const CockpitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  background: ${STATUS_COLORS.green.bg};
  border: 1px solid rgba(35, 122, 80, 0.3);
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${STATUS_COLORS.green.fg};
`;

export const CockpitBadgeDot = styled.span`
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: ${SEMAPHORE.green.fill};
  flex-shrink: 0;
`;

export const CockpitTitle = styled.h1`
  margin: 14px 0 0;
  font-size: 33px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: ${TEXT_VALUE};
`;

export const CockpitSubtitle = styled.div`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: ${INK.quiet};
`;

export const CockpitHeaderRight = styled.div`
  flex-shrink: 0;
  text-align: right;
  margin-left: auto;
`;

export const CockpitOverline = styled.div`
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${TEXT_META};
  line-height: 1.3;
`;

export const CockpitRef = styled.div`
  margin-top: 0.45rem;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 0.68rem;
  color: ${TEXT_META};
  letter-spacing: 0.03em;
`;

export const CockpitDate = styled.div`
  margin-top: 0.35rem;
  font-size: 0.68rem;
  color: ${TEXT_META};
  opacity: 0.85;
`;

export const MapImage = styled.div<{ $url?: string }>`
  width: 100%;
  height: 200px;
  border-radius: 0.75rem;
  background: ${(p) => (p.$url ? `url(${p.$url})` : SURFACE_GREY)};
  background-size: cover;
  background-position: center;
  margin-bottom: 0.5rem;
  position: relative;
`;

export const MapPin = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
  font-size: 2rem;
`;

// ── Section chips row (below header) ─────────────────────────────
export const SectionChipsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

export const SectionChip = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(p) => (p.$active ? "#111827" : SURFACE_GREY)};
  color: ${(p) => (p.$active ? WHITE : "#6b7280")};
  border: 1px solid ${(p) => (p.$active ? "#111827" : BORDER_GREY)};

  &:hover {
    opacity: 0.8;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const AddressCaption = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: ${TEXT_META};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
`;

// ── Section ──────────────────────────────────────────────────────
export const SectionWrapper = styled.div<{ $visible?: boolean }>`
  margin-bottom: 2rem;
  /* Anchor links (#section-N) land here. Push the scroll target down by the
     sticky subheader's approximate height so the section eyebrow ends up
     fully visible instead of hidden under the bar. */
  scroll-margin-top: 80px;
  opacity: ${(p) => (p.$visible === false ? 0 : 1)};
  transform: translateY(${(p) => (p.$visible === false ? "20px" : "0")});
  transition: opacity 0.5s ease, transform 0.5s ease;
`;

// Plain fallback header — still used for the urbanistic report, which MIKE-322
// didn't cover (different, mostly-placeholder section list). The unified
// legal-fiscal/final report uses the richer SectionHeader* set below instead.
export const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${BRAND_NAVY};
  margin-bottom: 0.25rem;
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${TEXT_HEADING};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.025em;
`;

// ── Section header (MIKE-322) ───────────────────────────────────────
// Replaces the old plain "Sección N / Title" pair with the design's own
// section-drawer header: icon badge tinted by verdict, "Sección N · Grupo"
// overline, bold title, verdict pill + orientativo/verificado tag.
export const BackToSummaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.375rem;
  padding: 0.5rem 0.9375rem 0.5rem 0.75rem;
  background: ${TINT_GREEN[100]};
  border: 1px solid ${BORDER_GREEN};
  border-radius: 100px;
  font-family: Inter, sans-serif;
  font-size: 0.781rem;
  font-weight: 600;
  color: ${SEMAPHORE.green.text};
  cursor: pointer;

  &:hover {
    background: ${TINT_GREEN[300]};
  }
`;

export const SectionHeaderRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.375rem;
`;

const SECTION_STATUS_TINTS: Record<string, { tint: string; color: string }> = {
  favorable: { tint: TINT_GREEN[400], color: SEMAPHORE.green.fill },
  reservas: { tint: TINT_AMBER, color: SEMAPHORE.amber.fill },
  critico: { tint: TINT_RED, color: SEMAPHORE.red.fill }
};

export const SectionHeaderIconBadge = styled.span<{ $status: string }>`
  flex-shrink: 0;
  margin-top: 0.125rem;
  width: 2.875rem;
  height: 2.875rem;
  border-radius: 0.8125rem;
  background: ${(p) => (SECTION_STATUS_TINTS[p.$status] || SECTION_STATUS_TINTS.favorable).tint};
  color: ${(p) => (SECTION_STATUS_TINTS[p.$status] || SECTION_STATUS_TINTS.favorable).color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SectionHeaderOverline = styled.div`
  font-size: 0.688rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${INK.label};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

export const SectionHeaderTitle = styled.h2`
  font-family: ${FONT_FIGTREE};
  font-size: 1.375rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: ${TEXT_HEADING};
  margin: 0;
`;

export const SectionHeaderMetaRow = styled.div`
  margin-top: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

export const SectionStatusPill = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  white-space: nowrap;
  padding: 0.25rem 0.625rem;
  background: ${(p) => (SECTION_STATUS_TINTS[p.$status] || SECTION_STATUS_TINTS.favorable).tint};
  border-radius: 100px;
  font-size: 0.688rem;
  font-weight: 600;
  color: ${(p) => (SECTION_STATUS_TINTS[p.$status] || SECTION_STATUS_TINTS.favorable).color};
`;

export const SectionFiabTag = styled.span`
  font-size: 0.688rem;
  color: ${INK.faint};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// ── Subsection card ──────────────────────────────────────────────
export const SubsectionCard = styled.div`
  background: ${WHITE};
  border: 1px solid ${CARD_BORDER};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 1rem;
  box-shadow: none;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  color: ${TEXT_HEADING};
  line-height: 24px;
  scroll-margin-top: 80px;

  &:hover {
    box-shadow: rgba(0, 27, 46, 0.1) 0px 4px 16px 0px, rgba(0, 27, 46, 0.06) 0px 8px 24px 0px;
  }
`;

export const SubsectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const SubsectionNumber = styled.span`
  background: ${BRAND_NAVY};
  color: ${WHITE};
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
  min-width: 1.5rem;
  text-align: center;
`;

export const SubsectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${TEXT_HEADING};
  line-height: 1.25;
  margin: 0;
`;

// ── Quick summary box ────────────────────────────────────────────
export const QuickSummaryBox = styled.div`
  background: ${SURFACE_GREY};
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

export const QuickSummaryLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${TEXT_META};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

// ── Data pills (grid of metric cards) ────────────────────────────
export const DataPillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const DataPill = styled.div`
  background: ${SURFACE_GREY_DEEP};
  border: 1px solid ${CARD_BORDER};
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

export const DataPillIcon = styled.div`
  font-size: 1rem;
  color: ${TEXT_META};
  margin-bottom: 0.25rem;
`;

export const DataPillValue = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${TEXT_HEADING};
  margin-bottom: 0.15rem;
`;

export const DataPillLabel = styled.div`
  font-size: 0.65rem;
  color: ${TEXT_META};
  line-height: 1.2;
  margin-top: auto;
`;

// ── Category cards (title top-left, body below) ─────────────────
// Used when a subsection needs wider cards with a category header
// and a descriptive text, instead of compact value-label pills.
export const CategoryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const CategoryCard = styled.div`
  background: transparent;
  border: 1px solid ${CARD_BORDER};
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const CategoryCardLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${TEXT_META};
`;

export const CategoryCardText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${TEXT_HEADING};
  line-height: 1.2em;
  text-align: left;
`;

// ── Status badges ────────────────────────────────────────────────
// The badge palette carries a fourth state the semaphore has no color for,
// and the payload spells it "grey" — not the semaphore's "neutral".
// The badge palette's fourth state, spelled "grey" in the payload.
const BADGE_GREY = "grey" as const;
export const BADGE_VARIANTS = [...SEMAPHORE_TONES, BADGE_GREY] as const;
export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export const StatusBadge = styled.span<{ $variant: BadgeVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  background: ${(p) => STATUS_COLORS[p.$variant as BadgeVariant].bg};
  color: ${(p) => STATUS_COLORS[p.$variant as BadgeVariant].fg};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

// ── Viability badge (large) ──────────────────────────────────────
export const ViabilityBadge = styled.div<{ $variant: BadgeVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
  border-radius: 0.5rem;
  background: ${(p) => STATUS_COLORS[p.$variant as BadgeVariant].bg};
  color: ${(p) => STATUS_COLORS[p.$variant as BadgeVariant].fg};
  margin-bottom: 0.5rem;
`;

// ── Accent heading (left green pipe, reusable across full-analysis blocks) ─
export const AccentHeading = styled.h4`
  margin: 2rem 0 0.5rem 0;
  padding: 0.15rem 0 0.15rem 0.75rem;
  border-left: 3px solid ${STATUS_COLORS.green.fg};
  font-size: 0.875rem;
  font-weight: 700;
  color: ${TEXT_HEADING};
  line-height: 1.3;

  &:first-child {
    margin-top: 0;
  }
`;

// Rounded container that wraps a detail table. The AccentHeading lives outside
// (above) so its pipe aligns flush with the box's left edge.
export const AccentTableBox = styled.div`
  border: 1px solid ${CARD_BORDER};
  border-radius: 12px;
  padding: 0;
  background: ${WHITE};
  overflow: hidden;
  margin-bottom: 1rem;
`;

// ── Key points table (the "Kv" block pattern, MIKE-322) ────────────
// A bordered box of label/value rows — uppercase label left, bold value
// right, hairline separators. Reused for the "kv" block pattern below
// (was previously defined but never wired up from BlockRenderer).
// A kv group's own caption ("Identificación catastral"), when one block
// holds several groups in a row — the design's drawer `g.heading`.
export const KvTitle = styled.div`
  margin: 1.5rem 0 0.75rem;
  font-size: 0.656rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${INK.label};
`;

export const KeyPointsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${BORDER_SUBTLE} !important;
  border-radius: 0.8125rem;
  overflow: hidden;
  margin: 0.125rem 0 0.875rem;
  box-shadow: 0 0 0 1px ${BORDER_SUBTLE};
`;

export const KeyPointRow = styled.tr`
  border-bottom: 1px solid ${BORDER_SUBTLE};

  &:last-child {
    border-bottom: none;
  }
`;

export const KeyPointCategory = styled.td`
  padding: 0.688rem 0.9375rem;
  font-size: 0.656rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK.label};
  white-space: nowrap;
  vertical-align: baseline;
`;

const KV_TONES: Record<string, string> = {
  green: SEMAPHORE.green.text,
  amber: SEMAPHORE.amber.text,
  red: SEMAPHORE.red.text,
  neutral: TEXT_VALUE
};

export const KeyPointValue = styled.td<{ $tone?: string }>`
  padding: 0.688rem 0.9375rem 0.688rem 0;
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => KV_TONES[p.$tone || "neutral"] || KV_TONES.neutral};
  text-align: right;
  vertical-align: baseline;
`;

// ── Actions list ─────────────────────────────────────────────────
export const ActionsBlock = styled.div<{ $variant: "essential" | "recommended" }>`
  border-left: 3px solid ${(p) => (p.$variant === "essential" ? STATUS_COLORS.amber.fg : STATUS_COLORS.green.fg)};
  padding: 0.5rem 1rem;
  margin-bottom: 0.5rem;
`;

export const ActionsTitle = styled.div<{ $variant: "essential" | "recommended" }>`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${(p) => (p.$variant === "essential" ? STATUS_COLORS.amber.fg : STATUS_COLORS.green.fg)};
  margin-bottom: 0.5rem;
`;

export const ActionItem = styled.div`
  font-size: 0.875rem;
  color: ${TEXT_BODY};
  margin-bottom: 0.35rem;
  display: flex;
  gap: 0.4rem;

  &::before {
    content: "\\25CF";
    color: ${STATUS_COLORS.green.fg};
    flex-shrink: 0;
    transition: transform 0.15s ease;
    transform-origin: center;
    display: inline-block;
  }

  &:hover::before {
    transform: scale(1.35);
  }
`;

// ── Info note box ────────────────────────────────────────────────
export const InfoNote = styled.div`
  background: ${STATUS_COLORS.green.bg};
  border-radius: 0.5rem;
  padding: 1.5rem 1rem;
  font-size: 0.75rem;
  line-height: 19px;
  color: ${TEXT_BODY};
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin: 0.5rem 0 1rem 0;

  strong {
    font-size: 1.2em;
    color: ${TEXT_HEADING};
  }
`;

// ── Alert box (diagnostic / recommendation callouts) ─────────────
type AlertVariant = "warning" | "success" | "info" | "error";

const ALERT_PALETTE: Record<AlertVariant, { bg: string; fg: string }> = {
  warning: STATUS_COLORS.amber,
  success: STATUS_COLORS.green,
  info: STATUS_COLORS.grey,
  error: STATUS_COLORS.red,
};

export const AlertBox = styled.div<{ $variant: AlertVariant }>`
  background: ${(p) => ALERT_PALETTE[p.$variant as AlertVariant].bg};
  border: 1px solid ${(p) => ALERT_PALETTE[p.$variant as AlertVariant].fg};
  border-radius: 12px;
  padding: 1rem;
  color: ${TEXT_BODY};
  font-size: 0.875rem;
  line-height: 1.45;
  margin: 0.5rem 0;
`;

export const AlertTitle = styled.div<{ $variant: AlertVariant }>`
  font-weight: 700;
  color: ${(p) => ALERT_PALETTE[p.$variant as AlertVariant].fg};
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

// ── Accordion body (smooth height transition) ────────────────────
// Uses the grid-template-rows 0fr→1fr trick to animate unknown heights
// without JS measurements. 0.25s matches the design.
export const AccordionBody = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease, margin-top 0.25s ease;
  overflow: hidden;
  margin-top: ${(p) => (p.$open ? "2rem" : "0")};

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

// ── Expand toggle ────────────────────────────────────────────────
export const ExpandToggle = styled.button`
  background: none;
  border: none;
  color: ${TEXT_HEADING};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    opacity: 0.7;
  }
`;

// ── Summary text ─────────────────────────────────────────────────
export const SummaryText = styled.p`
  font-size: 0.875rem;
  color: ${TEXT_BODY};
  line-height: 1.5;
  margin: 0.5rem 0;
`;

// ── Documentation table ──────────────────────────────────────────
// `$compact` reproduces the design's information table: 11.5px body and a
// 9.5px uppercase header. Without it the table keeps the older, larger scale
// used by v1 reports.
export const DocTable = styled.table<{ $compact?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  font-size: ${(p) => (p.$compact ? "0.72rem" : "0.875rem")};

  ${(p) =>
    p.$compact &&
    `
    thead th {
      font-size: 0.594rem;
      letter-spacing: 0.04em;
      padding: 0.69rem 0.81rem;
    }

    td {
      padding: 0.69rem 0.81rem;
    }
  `}
`;

export const DocTableHead = styled.thead`
  background: ${SURFACE_GREY};

  th {
    padding: 0.7rem 0.9rem;
    text-align: left;
    font-weight: 700;
    color: ${TEXT_HEADING};
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

export const DocTableRow = styled.tr`
  border-bottom: 1px solid ${BORDER_GREY};
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f3fcf8;
  }
`;

export const DocTableCell = styled.td`
  padding: 0.7rem 0.9rem;
  color: ${TEXT_BODY};
`;

// Criticality pill (MIKE-322): a tinted pill carrying its own label
// ("Alta"/"Media"/"Baja"), replacing the old plain colored dot.
export const CriticalityPill = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 0.1875rem 0.5625rem;
  border-radius: 100px;
  font-size: 0.594rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  white-space: nowrap;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

// ── Loading spinner ──────────────────────────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  border: 2px solid ${BORDER_GREY};
  border-top-color: ${BRAND_GREEN};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  display: inline-block;
`;

// ── Loading state for sections ───────────────────────────────────
export const SectionLoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  max-width: 34rem;
  margin: 0 auto;
  text-align: center;
  line-height: 1.5;
  font-size: 0.875rem;
  color: ${TEXT_META};
  padding: 2rem 0;
`;

// ── Error state ──────────────────────────────────────────────────
export const SectionErrorState = styled.div`
  font-size: 0.875rem;
  color: ${TEXT_META};
  padding: 0.5rem 1rem;
  background: ${SURFACE_GREY};
  border-radius: 0.5rem;
  border-left: 3px solid ${BORDER_GREY};
`;

// ── Locked section (unpaid) ──────────────────────────────────────
export const LockedSectionCard = styled.div`
  background: #f9fafb;
  border: 1.5px dashed ${BORDER_DASHED};
  border-radius: 0.75rem;
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const LockedSectionIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${SURFACE_GREY};
  border: 1px solid ${BORDER_GREY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${TEXT_PLACEHOLDER};
`;

export const LockedSectionText = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  max-width: 28rem;
`;

export const LockedSectionCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #111827;
  color: ${WHITE};
  border: none;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  margin-top: 0.25rem;

  &:hover {
    opacity: 0.9;
  }
`;

// ── Section the tier does not cover ──────────────────────────────
// Seeded by the backend as a `blocked` placeholder carrying `lockedContent`:
// it advertises what a higher level would unlock, so it reads as an offer
// rather than as an error.
export const TierLockedCard = styled.div`
  margin-top: 2rem;
  padding: 3rem 1.75rem;
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
`;

export const TierLockedIcon = styled.div`
  color: ${INK.label};
  line-height: 0;
`;

export const TierLockedTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${TEXT_HEADING};
`;

export const TierLockedText = styled.p`
  margin: 0;
  max-width: 28rem;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${TEXT_META};
`;

export const TierLockedAvailability = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: ${TEXT_VALUE};
  color: ${WHITE};
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.625rem;
`;

// Wrapper that blurs its children (section 1 blocks in unpaid state).
// pointer-events + user-select block copy-paste. The border-radius + overflow
// clip keep the blur from bleeding into adjacent layout.
export const BlurredContentWrapper = styled.div`
  filter: blur(5px);
  user-select: none;
  pointer-events: none;
  border-radius: 0.625rem;
  overflow: hidden;
`;

// Sections 2+: relative wrapper so the overlay can be positioned on top. The
// skeleton underneath can be as short as a single line, so the wrapper keeps
// enough height for the overlay's lock, copy and CTA to sit comfortably
// instead of spilling over a two-line section.
export const LockedSectionRelative = styled.div`
  position: relative;
  min-height: 16rem;
`;

// Block-level counterpart of LockedSectionRelative, for a single blurred block
// inside an otherwise visible section (a §2 teaser)
export const LockedBlockRelative = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

// Frosted-glass overlay for sections 2+. Sits on top of the blurred content
// and centers the lock copy + CTA.
export const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  border-radius: 0.625rem;
`;

// ── Disclaimer ───────────────────────────────────────────────────
export const Disclaimer = styled.div`
  background: ${SURFACE_GREY};
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 0.75rem;
  color: ${TEXT_META};
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
`;

export const DisclaimerLink = styled.a`
  font-size: 0.75rem;
  color: ${CHROME_INK};
  text-decoration: underline;

  &:hover {
    color: ${BLACK};
  }
`;

// ── Settings modal (same pattern as Sidebar/Styles.ts) ───────────
export const SettingsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

export const SettingsContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  background-color: ${WHITE};
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

export const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${SURFACE_GREY};
  flex-shrink: 0;
`;

export const SettingsTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${TEXT_HEADING};
  margin: 0;
  letter-spacing: -0.025em;
`;

export const SettingsCloseButton = styled.button`
  padding: 0.5rem;
  border-radius: 9999px;
  border: none;
  background-color: transparent;
  color: ${TEXT_META};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${SURFACE_GREY};
    color: ${TEXT_BODY};
  }

  &:focus {
    outline: none;
  }
`;

export const SettingsBody = styled.div`
  overflow-y: auto;
  padding: 1.5rem;
  flex: 1;
`;

// ────────────────────────────────────────────────────────────────────
// Legal-Fiscal v2 — block-based renderer styles
// ────────────────────────────────────────────────────────────────────

export const SectionIconCircle = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: ${SURFACE_GREY};
  color: ${TEXT_HEADING};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SectionEyebrow = styled.div`
  font-size: 0.7rem;
  color: ${TEXT_META};
  text-transform: none;
  font-weight: 500;
  margin-bottom: 0.1rem;
`;

export const SectionHeading = styled.h2`
  font-family: ${FONT_REPORT};
  font-size: 1.35rem;
  font-weight: 600;
  color: ${TEXT_HEADING};
  margin: 0;
  line-height: 1.2;
`;

export const BlockCard = styled.div`
  background: ${WHITE};
  border-radius: 0.875rem;
  margin-bottom: 1rem;
  overflow: hidden;
`;

export const BlockHeaderRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1.1rem 1.35rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: inherit;

  &:hover {
    background: rgba(0, 27, 46, 0.02);
  }
`;

export const BlockNumberPill = styled.span`
  font-family: ${FONT_FIGTREE};
  background: ${WASH};
  border: 1px solid ${BORDER_GREEN};
  color: ${GREEN_ACCENT};
  font-size: 0.625rem;
  font-weight: 700;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: center;
`;

// Wraps the number tag + title (and the collapsed preview underneath) so the
// chevron stays vertically centred against the whole stack.
export const BlockHeaderMain = styled.span`
  flex: 1;
  min-width: 0;
`;

export const BlockTitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

export const BlockTitle = styled.span`
  font-family: ${FONT_FIGTREE};
  font-size: 1rem;
  font-weight: 800;
  color: ${TEXT_VALUE};
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

// Only shown while the block is collapsed: a one-line hint of what is inside.
export const BlockPreview = styled.span`
  display: block;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  color: #94a0a5;
  line-height: 1.4;
`;

export const BlockChevron = styled.span`
  color: ${TEXT_META};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

// Left padding lines the body content up under the title, not under the
// number badge — matches the design: the badge sits in its own column with
// nothing below it, the whole body indents to the title's left edge instead
// (1.35rem header padding + 2.25rem badge + 0.7rem gap = 4.3rem).
export const BlockBody = styled.div<{ $open: boolean }>`
  padding: ${(p) => (p.$open ? "0 1.35rem 1.35rem 4.3rem" : "0 1.35rem")};
  max-height: ${(p) => (p.$open ? "none" : "0")};
  overflow: hidden;
  transition: padding 0.2s ease;
`;

// ── Ribbon labels (LO ESENCIAL / DATOS CLAVE / etc.) ────────────────
export const RibbonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 1.35rem 0 0.55rem 0;
`;

export const RibbonLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: ${TEXT_META};
  text-transform: uppercase;
`;

// Design handoff variant used for the lead paragraph of registral blocks:
// bold text with the thin green rule on the left.
export const EssenceAccent = styled.p`
  margin: 0.625rem 0 0.9rem;
  padding: 0.1rem 0 0.1rem 0.9rem;
  border-left: 3px solid ${SEMAPHORE.green.fill};
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.55;
  color: ${TEXT_VALUE};
  text-wrap: pretty;
`;

// ── Key data strip (DATOS CLAVE) ────────────────────────────────────
// A continuous row on a soft gradient — items are divided by a colored
// left border + dot rather than being individual cards.
// ── Synth strip (MIKE-322 follow-up) ────────────────────────────────
// The horizontal row of headline figures shown ONCE PER SECTION, right below
// the section header — NOT per block. (Originally built for the per-block
// "Datos clave" field, which actually renders as the vertical KeyPointsTable
// list below — see KeyDataGrid/renderKeyData in BlockRenderer.)
export const SynthStrip = styled.div`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  background: linear-gradient(180deg, ${SURFACE_CARD}, #f5f9f6);
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 1rem;
  padding: 1rem 0.25rem;
  margin: 0.4rem 0 1.375rem;
`;

const SYNTH_TONES: Record<string, { dot: string; value: string }> = {
  green: { dot: SEMAPHORE.green.fill, value: SEMAPHORE.green.text },
  amber: { dot: SEMAPHORE.amber.fill, value: SEMAPHORE.amber.text },
  red: { dot: SEMAPHORE.red.fill, value: SEMAPHORE.red.text },
  neutral: {
    dot: SEMAPHORE_WITH_NEUTRAL.neutral.fill,
    value: SEMAPHORE_WITH_NEUTRAL.neutral.text
  }
};

export const SynthItem = styled.div<{ $first?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 0.125rem 1.125rem;
  border-left: ${(p) => (p.$first ? "none" : `1px solid ${BORDER_SUBTLE}`)};
`;

export const SynthLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.4375rem;
`;

export const SynthDot = styled.span<{ $tone: string }>`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (SYNTH_TONES[p.$tone] || SYNTH_TONES.neutral).dot};
`;

export const SynthLabel = styled.div`
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${INK.label};
  text-transform: uppercase;
  white-space: nowrap;
`;

export const SynthValue = styled.div<{ $tone: string }>`
  font-family: ${FONT_FIGTREE};
  font-size: 0.90625rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (SYNTH_TONES[p.$tone] || SYNTH_TONES.neutral).value};
`;

// ── Section-level callout (MIKE-322) — one highlighted takeaway box per
// section, right after the synth strip, before its blocks (dc.html
// drawer.callouts). Dot + title + text, tone-colored — distinct from a
// block's own `callout` (the icon-badge variant, see CalloutBox below).
const SECTION_CALLOUT_TONES: Record<string, { bg: string; border: string; dot: string }> = {
  favorable: { bg: TINT_GREEN[50], border: BORDER_GREEN, dot: SEMAPHORE.green.fill },
  reservas: { bg: TINT_AMBER, border: BORDER_TINT.amber, dot: SEMAPHORE.amber.fill },
  critico: { bg: TINT_RED, border: BORDER_TINT.red, dot: SEMAPHORE.red.fill },
  neutral: {
    bg: SURFACE_CARD,
    border: BORDER_SUBTLE,
    dot: SEMAPHORE_WITH_NEUTRAL.neutral.fill
  },
};

export const SectionCalloutBox = styled.div<{ $tone?: string }>`
  margin-top: 1.25rem;
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.125rem;
  background: ${(p) => (SECTION_CALLOUT_TONES[p.$tone || "neutral"] || SECTION_CALLOUT_TONES.neutral).bg};
  border: 1px solid ${(p) => (SECTION_CALLOUT_TONES[p.$tone || "neutral"] || SECTION_CALLOUT_TONES.neutral).border};
  border-radius: 0.8125rem;
`;

export const SectionCalloutDot = styled.span<{ $tone?: string }>`
  flex-shrink: 0;
  margin-top: 0.375rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(p) => (SECTION_CALLOUT_TONES[p.$tone || "neutral"] || SECTION_CALLOUT_TONES.neutral).dot};
`;

export const SectionCalloutTitle = styled.div`
  font-family: ${FONT_FIGTREE};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${TEXT_LEAD};
`;

export const SectionCalloutText = styled.div`
  margin-top: 0.1875rem;
  font-size: 0.8125rem;
  color: ${INK.soft};
  line-height: 1.55;
`;

// ── Key data ("Datos clave") — hero-fact card grid ──────────────────
// Matches the design's drawer.cat.heroFacts 1:1: a 3-column grid of cards,
// each with an icon badge, an uppercase label, a big Figtree value, and a
// gray sub-caption. 'green' (the default) is a plain/near-white card with a
// dark value — only 'amber'/'red' actually tint the card and color the value.
const FACT_TONES: Record<string, { bg: string; border: string; icon: string; value: string }> = {
  green: {
    bg: SURFACE_CARD, border: BORDER_SUBTLE,
    icon: SEMAPHORE.green.fill, value: TEXT_VALUE
  },
  neutral: {
    bg: SURFACE_CARD, border: BORDER_SUBTLE,
    icon: SEMAPHORE_WITH_NEUTRAL.neutral.fill, value: TEXT_VALUE
  },
  amber: {
    bg: TINT_AMBER, border: BORDER_TINT.amber,
    icon: SEMAPHORE.amber.fill, value: SEMAPHORE.amber.text
  },
  red: {
    bg: TINT_RED, border: BORDER_TINT.red,
    icon: SEMAPHORE.red.fill, value: SEMAPHORE.red.text
  }
};

const factTone = (tone?: string) => FACT_TONES[tone || "green"] || FACT_TONES.green;

export const HeroFactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.625rem;
  margin: 0.5rem 0 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroFactCard = styled.div<{ $tone?: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.9375rem;
  border-radius: 0.8125rem;
  background: ${(p) => factTone(p.$tone).bg};
  border: 1px solid ${(p) => factTone(p.$tone).border};
`;

export const HeroFactIconBadge = styled.span<{ $tone?: string }>`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.5rem;
  background: ${WHITE};
  border: 1px solid ${(p) => factTone(p.$tone).border};
  color: ${(p) => factTone(p.$tone).icon};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeroFactLabel = styled.div`
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${INK.label};
`;

export const HeroFactValue = styled.div<{ $tone?: string }>`
  margin-top: 0.25rem;
  font-family: ${FONT_FIGTREE};
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${(p) => factTone(p.$tone).value};
`;

export const HeroFactSub = styled.div`
  margin-top: 0.1875rem;
  font-size: 0.656rem;
  color: ${INK.quiet};
  line-height: 1.35;
`;

// ── Chips (MIKE-322, dc.html "chips" block type) — a wrapping row of small
// pill cards, each a tiny uppercase label over a bold value. No icon, no
// fixed grid — distinct from heroFacts/specs above.
export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 0.875rem;
`;

export const ChipCard = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.5rem 0.8125rem;
  background: ${WASH};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.625rem;
`;

export const ChipLabel = styled.span`
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${INK.label};
`;

export const ChipValue = styled.span`
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
`;

// The design's `docs` block: one full-width row per document — icon, title and
// meta on the left, the action on the right — not a grid of cards.
export const DocumentCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;
export const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.8rem 1rem;
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.75rem;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;
export const DocumentCardIcon = styled.span`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.55rem;
  background: ${WASH};
  border: 1px solid ${BORDER_GREEN};
  color: ${GREEN_ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const DocumentCardBody = styled.div`
  flex: 1;
  min-width: 0;
`;
export const DocumentCardTitle = styled.div`
  margin-bottom: 0.3rem;
  font-family: ${FONT_FIGTREE};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
`;
export const DocumentCardText = styled.div`
  font-size: 0.72rem;
  line-height: 1.5;
  color: ${INK.muted};
`;

export const EntityCard = styled.div<{ $tone?: string }>`
  padding: 1.1rem 1.25rem;
  background: ${(p) => (p.$tone === "amber" ? TINT_AMBER : TINT_GREEN[100])};
  border: 1px solid ${(p) => (p.$tone === "amber" ? BORDER_TINT.amber : BORDER_GREEN)};
  border-radius: 0.875rem;
`;
export const EntityName = styled.div`
  font-family: ${FONT_FIGTREE}; font-size: 1rem; font-weight: 800; color: ${TEXT_VALUE};
`;
export const EntityMeta = styled.div<{ $tone?: string }>`
  margin-top: 0.15rem; font-family: ui-monospace, monospace; font-size: 0.72rem; color: ${(p) => (p.$tone === "amber" ? AMBER_INK : "#5a8c6f")};
`;

// ── Specs list (dc.html: drawer.cat.specs) ──────────────────────────
// Icon + fixed-width uppercase label + LEFT-aligned value, one per row in a
// bordered box. Visually distinct from the "kv" block pattern (KeyPointsTable
// below): kv right-aligns its value and has no icon; this doesn't.
export const SpecsBox = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.8125rem;
  overflow: hidden;
  margin: 0.125rem 0 0.5rem;
`;

export const SpecRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6875rem 0.9375rem;
  border-bottom: 1px solid ${BORDER_SUBTLE};

  &:last-child {
    border-bottom: none;
  }
`;

export const SpecIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  color: ${INK.ghost};
`;

export const SpecLabel = styled.span`
  flex-shrink: 0;
  width: 9.0625rem;
  font-size: 0.656rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK.label};
`;

export const SpecValue = styled.span<{ $tone?: string }>`
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => factTone(p.$tone).value};
`;

export const PlainSpecRow = styled.div`
  display: grid;
  grid-template-columns: minmax(9rem, 0.8fr) 1.5fr;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  border-bottom: 1px solid ${BORDER_SUBTLE};
  &:last-child { border-bottom: none; }
`;

export const PlainSpecLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
`;

export const TimelineSpecRow = styled(PlainSpecRow)`
  grid-template-columns: 8.5rem 1fr;
`;
export const TimelineSpecLabel = styled(PlainSpecLabel)`
  color: ${SEMAPHORE.green.text};
`;

export const PlainSpecValue = styled.span<{ $tone?: string }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(p) => p.$tone === TONE.amber ? SEMAPHORE.amber.text : p.$tone === TONE.green ? SEMAPHORE.green.text : INK.body};
`;

export const MutedUpperSpecLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK.faint};
`;

// Plain source/caption line under a keyData list (dc.html line 687). Not
// boxed — a property of the list, so it works under any block's keyData.
export const SpecsFooter = styled.div`
  margin-top: 0.625rem;
  font-size: 0.656rem;
  color: ${INK.faint};
  line-height: 1.5;
`;

// ── Prose subsection (id + h4 title [+ tag] / narrative body) ──────
// The most common content pattern in the redesign: a two-column subsection
// with a slim label column (overline id, title, optional tone pill) and a
// prose column on the right whose first paragraph is bolded.
export const ProseSection = styled.div`
  display: grid;
  grid-template-columns: 168px 1fr;
  gap: 1.75rem;
  align-items: start;
  border-top: 1px solid ${BORDER_SUBTLE};
  padding-top: 0.95rem;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }

  & + & {
    margin-top: 1rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

export const FullSubsection = styled.div`
  border-top: 1px solid ${BORDER_SUBTLE};
  padding: 1rem 0 1.1rem;
`;
export const FullSubsectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  margin-bottom: 0.65rem;
`;
export const FullSubsectionNumber = styled.span`
  color: ${INK.faint}; font-weight: 700; margin-right: 0.4rem;
`;
const SUBSECTION_STATUS_TONES: Record<string, { bg: string; color: string }> = {
  green: { bg: TINT_GREEN[200], color: SEMAPHORE.green.text },
  amber: { bg: TINT_AMBER, color: SEMAPHORE.amber.text },
  red: { bg: TINT_RED_PINK, color: SEMAPHORE.red.text },
  neutral: { bg: TINT_NEUTRAL, color: SEMAPHORE_WITH_NEUTRAL.neutral.text }
};

export const SubsectionStatus = styled.span<{ $tone?: string }>`
  padding: 0.16rem 0.6rem; border-radius: 999px; font-size: 0.68rem; font-weight: 700;
  color: ${(p) => (SUBSECTION_STATUS_TONES[p.$tone || "green"] || SUBSECTION_STATUS_TONES.green).color};
  background: ${(p) => (SUBSECTION_STATUS_TONES[p.$tone || "green"] || SUBSECTION_STATUS_TONES.green).bg};
`;
export const LeadStatusPill = styled.div`
  display: flex; align-items: center; gap: 0.7rem; padding: 0.8rem 1.1rem;
  margin: 0.15rem 0 0.9rem; background: ${TINT_GREEN[200]}; border: 1px solid ${BORDER_TINT.green}; border-radius: 0.75rem;
  font-family: ${FONT_FIGTREE}; font-size: 0.95rem; font-weight: 800; color: ${TEXT_VALUE};
`;
export const LeadStatusIcon = styled.span`
  width: 1.4rem; height: 1.4rem; border-radius: 50%; background: ${SEMAPHORE.green.fill}; color: ${WHITE};
  display: inline-flex; align-items: center; justify-content: center;
`;

export const ProseLabelCol = styled.div`
  min-width: 0;
`;

export const ProseOverline = styled.div`
  font-family: ${FONT_FIGTREE};
  font-size: 0.656rem;
  font-weight: 700;
  color: ${INK.faint};
  margin-bottom: 0.375rem;
`;

export const ProseTitle = styled.h4`
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${INK.strong};
  letter-spacing: -0.01em;
  margin: 0;
  line-height: 1.32;
`;

const PROSE_TAG_TONES: Record<string, { bg: string; color: string }> = {
  green: { bg: TINT_GREEN[200], color: SEMAPHORE.green.text },
  amber: { bg: TINT_AMBER, color: SEMAPHORE.amber.text },
  red: { bg: TINT_RED_PINK, color: SEMAPHORE.red.text },
  neutral: { bg: TINT_NEUTRAL, color: SEMAPHORE_WITH_NEUTRAL.neutral.text }
};

export const ProseTagPill = styled.span<{ $tone: string }>`
  display: inline-block;
  margin-top: 0.6rem;
  padding: 0.19rem 0.625rem;
  border-radius: 100px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: ${(p) => (PROSE_TAG_TONES[p.$tone] || PROSE_TAG_TONES.neutral).bg};
  color: ${(p) => (PROSE_TAG_TONES[p.$tone] || PROSE_TAG_TONES.neutral).color};
`;

export const ProseBody = styled.div`
  min-width: 0;
`;

export const ProseParagraph = styled.p<{ $lead?: boolean }>`
  margin: 0 0 0.5rem;
  text-wrap: pretty;

  &:last-child {
    margin-bottom: 0;
  }

  ${(p) =>
    p.$lead
      ? `
    font-size: 0.86rem;
    font-weight: 600;
    line-height: 1.52;
    color: #2c4a52;
    letter-spacing: -0.005em;
  `
      : `
    font-size: 0.797rem;
    line-height: 1.62;
    color: #63747a;
  `}
`;

export const SubBlock = styled.div`
  margin: 1.25rem 0 0.35rem;
  padding-top: 1rem;
  border-top: 1px solid ${BORDER_SUBTLE};
`;

export const SubBlockHeading = styled.h4`
  margin: 0 0 0.75rem;
  font-family: ${FONT_FIGTREE};
  font-size: 0.84rem;
  font-weight: 700;
  color: ${INK.strong};
`;
export const SubBlockNumber = styled.span`
  color: ${INK.faint};
  font-weight: 600;
  margin-right: 0.45rem;
`;

// ── Callout, icon variant ("Aplicado a esta finca" / "Conclusión") ─
const CALLOUT_TONES = {
  applied: { bg: TINT_GREEN[50], border: "#e1eded" },
  conclusion: { bg: TINT_GREEN[100], border: BORDER_GREEN }
} as const;

export const CalloutBox = styled.div<{ $kind: "applied" | "conclusion" }>`
  display: flex;
  gap: 0.8125rem;
  padding: 1rem 1.125rem;
  background: ${(p) => CALLOUT_TONES[p.$kind as keyof typeof CALLOUT_TONES].bg};
  border: 1px solid ${(p) => CALLOUT_TONES[p.$kind as keyof typeof CALLOUT_TONES].border};
  border-radius: 0.875rem;
  margin: 0.375rem 0 0.875rem;
`;

export const CalloutIcon = styled.span`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5625rem;
  background: ${SEMAPHORE.green.fill};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${WHITE};
`;

export const CalloutLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${SEMAPHORE.green.text};
  margin-bottom: 0.25rem;
`;

export const CalloutTitle = styled.div`
  font-family: ${FONT_FIGTREE};
  font-size: 0.875rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
`;

export const CalloutText = styled.p<{ $lead?: boolean }>`
  margin: 0.3125rem 0 0;
  font-size: 0.781rem;
  line-height: 1.6;
  color: ${(p) => (p.$lead ? TEXT_LEAD : INK.body)};
  font-weight: ${(p) => (p.$lead ? 600 : 400)};
`;

// ── Note (subtle aside) ──────────────────────────────────────────────
// A caveat or scope note, not a finding: a quiet bordered box with a
// diamond marker — deliberately the visual opposite of ObservationItem
// (bulleted line, no box) so the two concepts don't blur together.
export const NoteBox = styled.div`
  display: flex;
  gap: 0.625rem;
  padding: 0.75rem 0.9375rem;
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.6875rem;
  margin: 0.125rem 0 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #6b7a80;
`;

export const NoteMarker = styled.span`
  flex-shrink: 0;
  color: ${INK.ghost};
`;

// ── Ledger (big-amounts list) ───────────────────────────────────────
export const LedgerTitle = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${INK.label};
  margin: 0.5rem 0 0.5625rem;
`;

export const LedgerBox = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  overflow: hidden;
  margin: 0.125rem 0 1rem;
`;

export const LedgerRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1.125rem;
  align-items: center;
  padding: 0.8125rem 1.0625rem;
  border-top: 1px solid ${BORDER_SUBTLE};

  &:first-child {
    border-top: none;
  }
`;

export const LedgerLabel = styled.span`
  display: block;
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
`;

export const LedgerSub = styled.span`
  display: block;
  margin-top: 0.1875rem;
  font-size: 0.719rem;
  color: ${INK.muted};
  line-height: 1.45;
  text-wrap: pretty;
`;

const LEDGER_VALUE_TONES: Record<string, string> = {
  green: SEMAPHORE.green.text,
  amber: SEMAPHORE.amber.text,
  neutral: TEXT_VALUE
};

export const LedgerValue = styled.span<{ $tone?: string }>`
  font-family: ${FONT_FIGTREE};
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  white-space: nowrap;
  color: ${(p) => LEDGER_VALUE_TONES[p.$tone || "neutral"] || LEDGER_VALUE_TONES.neutral};
`;

// ── Matrix (comparative table) ──────────────────────────────────────
export const MatrixBox = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  overflow: hidden;
  margin: 0.25rem 0 1rem;
`;

export const MatrixHeaderRow = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$cols === 2 ? "146px 1fr 1fr" : "118px 1fr 1fr 1fr")};
  background: ${WASH};
`;

export const MatrixHeaderCell = styled.div<{ $highlight?: boolean }>`
  padding: 0.75rem 0.9375rem;
  border-left: 1px solid ${BORDER_SUBTLE};
  background: ${(p) => (p.$highlight ? TINT_GREEN[100] : "transparent")};
`;

export const MatrixHeaderLabel = styled.span`
  display: block;
  font-family: ${FONT_FIGTREE};
  font-size: 0.75rem;
  font-weight: 800;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

export const MatrixHeaderTag = styled.span`
  display: inline-block;
  margin-top: 0.375rem;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${SEMAPHORE.green.text};
  /* A fill, not a rule: same value as BORDER_GREEN today, but if the
     hairline lightens this badge ground should not follow it. */
  background: #deeee5;
  border-radius: 0.3125rem;
  padding: 0.125rem 0.4375rem;
`;

export const MatrixRow = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$cols === 2 ? "146px 1fr 1fr" : "118px 1fr 1fr 1fr")};
  border-top: 1px solid ${BORDER_SUBTLE};
`;

export const MatrixRowLabel = styled.div`
  padding: 0.8125rem 0.9375rem;
  font-size: 0.594rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK.muted};
  line-height: 1.35;
`;

export const MatrixCell = styled.div<{ $highlight?: boolean }>`
  padding: 0.8125rem 0.9375rem;
  border-left: 1px solid ${BORDER_SUBTLE};
  background: ${(p) => (p.$highlight ? SURFACE_CARD : "transparent")};
  font-size: 0.75rem;
  line-height: 1.5;
  text-wrap: pretty;
  color: ${(p) => (p.$highlight ? INK.strong : INK.label)};
`;

// ── Cites (numbered reference list) ─────────────────────────────────
export const CitesBox = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.8125rem;
  overflow: hidden;
  margin: 0.125rem 0 0.5rem;
`;

export const CiteRow = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.5rem 0.875rem;
  border-top: 1px solid ${BORDER_SUBTLE};
  align-items: baseline;

  &:first-child {
    border-top: none;
  }
`;

export const CiteNumber = styled.span`
  flex-shrink: 0;
  width: 1.625rem;
  font-family: ${FONT_FIGTREE};
  font-weight: 700;
  color: ${INK.ghost};
  text-align: right;
  font-size: 0.688rem;
`;

export const CiteRef = styled.span`
  font-size: 0.75rem;
  color: ${INK.body};
  line-height: 1.5;
`;

// ── Finding card (MAPA DE HALLAZGOS) ────────────────────────────────
// Each category gets a tinted border + barely-there background. Mirrors the
// PDF/HTML reference: red-50 / amber-50 / emerald-50 / slate-50 family.
const FINDING_COLORS: Record<
  string,
  { dot: string; label: string; cardBorder: string; cardBg: string }
> = {
  high_risk: {
    dot: STATUS_COLORS.red.dot,
    label: "rgba(220, 38, 38, 0.85)",
    cardBorder: "rgba(252, 165, 165, 0.7)",
    cardBg: "rgba(254, 242, 242, 0.4)"
  },
  attention: {
    dot: STATUS_COLORS.amber.dot,
    label: "rgba(146, 64, 14, 0.9)",
    cardBorder: "rgba(252, 211, 77, 0.7)",
    cardBg: "rgba(255, 251, 235, 0.4)"
  },
  no_incidents: {
    dot: STATUS_COLORS.green.dot,
    label: "rgba(35, 122, 80, 0.9)",
    cardBorder: "rgba(110, 231, 183, 0.7)",
    cardBg: "rgba(236, 253, 245, 0.4)"
  },
  informative: {
    dot: STATUS_COLORS.grey.dot,
    label: "rgba(75, 85, 99, 0.9)",
    cardBorder: "rgba(203, 213, 225, 0.85)",
    cardBg: "rgba(241, 245, 249, 0.5)"
  },
  blocking: {
    dot: "#111827",
    label: "rgba(17, 24, 39, 0.9)",
    cardBorder: "rgba(100, 116, 139, 0.6)",
    cardBg: "rgba(241, 245, 249, 0.5)"
  }
};

export const FindingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const FindingCardWrapper = styled.div<{ $category?: string }>`
  border: 1px solid
    ${(p) => FINDING_COLORS[p.$category || "informative"]?.cardBorder || CARD_BORDER};
  border-radius: 0.75rem;
  padding: 0.95rem 1.1rem;
  background: ${(p) =>
    FINDING_COLORS[p.$category || "informative"]?.cardBg || WHITE};
`;

export const FindingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
`;

export const FindingCategory = styled.div<{ $category: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => FINDING_COLORS[p.$category]?.label || STATUS_COLORS.grey.fg};

  &::before {
    content: "";
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: ${(p) => FINDING_COLORS[p.$category]?.dot || STATUS_COLORS.grey.dot};
    display: inline-block;
  }
`;

export const FindingBadge = styled.span<{ $variant: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid
    ${(p) =>
      p.$variant === TONE.red
        ? "rgba(220, 38, 38, 0.35)"
        : p.$variant === TONE.amber
        ? "rgba(146, 64, 14, 0.3)"
        : p.$variant === TONE.green
        ? "rgba(35, 122, 80, 0.3)"
        : "rgba(107, 114, 128, 0.3)"};
  background: ${(p) => (STATUS_COLORS[p.$variant as BadgeVariant] ?? STATUS_COLORS.grey).bg};
  // The one value that is not the badge palette's: this badge's unknown-variant
  // ink is a step darker than STATUS_COLORS.grey.fg, and visibly so.
  color: ${(p) => STATUS_COLORS[p.$variant as BadgeVariant]?.fg ?? "#4b5563"};
`;

export const FindingTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${TEXT_HEADING};
  margin: 0.15rem 0 0.3rem 0;
`;

export const FindingDescription = styled.p`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${TEXT_BODY};
  margin: 0 0 0.4rem 0;
  line-height: 1.5;
`;

export const SubLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: ${TEXT_META};
  text-transform: uppercase;
  margin: 0.5rem 0 0.25rem 0;
`;

// Sources rendering has two modes:
//   - "all laws": every entry matches a known law/norm prefix (Art/Ley/RD/
//     BOE/STS/...). We render one chip per source with a balance-of-law icon.
//   - mixed: at least one entry is an internal reference (Sección X, Informe
//     Y, apartado Z…). The whole list collapses into a single grey pill with
//     · separators so the citation reads as a compound reference.
export const SourcesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.4rem;
`;

// Quiet section-level source footer from the design handoff: a divider, file
// icon and one muted line of provenance beneath the final block.
export const SectionSources = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  margin-top: 1.35rem;
  padding-top: 1rem;
  border-top: 1px solid ${BORDER_SUBTLE};
  font-size: 0.72rem;
  line-height: 1.55;
  color: ${INK.faint};

  svg { flex-shrink: 0; margin-top: 0.15rem; color: ${INK.ghost}; }
`;

export const SourceChip = styled.span<{ $kind?: "law" | "section" | "doc" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  line-height: 1.4;
  text-decoration: none;
  border: 1px solid transparent;

  ${(p) =>
    p.$kind === "section"
      ? `
    background: rgba(236, 253, 245, 0.4);
    border-color: rgba(35, 122, 80, 0.45);
    color: ${SEMAPHORE.green.text};

    svg {
      color: ${SEMAPHORE.green.text};
      opacity: 0.95;
      flex-shrink: 0;
    }
  `
      : `
    background: ${SURFACE_GREY};
    border-color: ${CARD_BORDER};
    color: ${TEXT_BODY};

    svg {
      color: ${TEXT_META};
      flex-shrink: 0;
    }
  `}

  &[href] {
    cursor: pointer;

    &:hover {
      ${(p) =>
        p.$kind === "section"
          ? "background: rgba(236, 253, 245, 0.7);"
          : `background: ${SURFACE_GREY_DEEP};`}
    }
  }
`;

export const SourcesGroupedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  color: ${TEXT_BODY};
  background: ${SURFACE_GREY};
  border: 1px solid ${CARD_BORDER};
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  line-height: 1.4;
`;

export const SourcesPillSeparator = styled.span`
  color: ${TEXT_META};
  opacity: 0.5;
`;

// ── Observations / OtherActions ─────────────────────────────────────
export const ObservationsBox = styled.div`
  margin: 0.4rem 0;
`;

export const ObservationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: ${TEXT_BODY};
  line-height: 1.55;
  padding: 0.3rem 0;

  &::before {
    content: "";
    flex-shrink: 0;
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background: ${BRAND_GREEN};
    margin-top: 0.5rem;
  }
`;

// ── Report meta header ──────────────────────────────────────────────
export const ReportMetaBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 2rem;
  font-size: 0.85rem;
  color: ${TEXT_META};
  padding: 0.35rem 0;
`;

export const ReportMetaLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: ${BRAND_GREEN};
  text-transform: uppercase;
  margin-right: 0.3rem;
`;

export const ReportTitleStrong = styled.span`
  font-weight: 600;
  color: ${TEXT_HEADING};
`;

// ── Figure (MIKE-322) ───────────────────────────────────────────────────────
// The design's `figs` block: one or three images inside a bordered card, each
// with its title and caption BELOW the image but inside the card. One column
// spans the content width; three columns is the side-by-side evidence grid.
export const FigureGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FigureCard = styled.figure`
  margin: 0;
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  overflow: hidden;
  background: ${WHITE};
`;

export const FigureImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const FigureCaption = styled.figcaption`
  padding: 0.75rem 0.9rem 0.85rem;
  border-top: 1px solid ${BORDER_SUBTLE};
`;

export const FigureCaptionTitle = styled.div`
  font-family: ${FONT_FIGTREE};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
  line-height: 1.35;
`;

export const FigureCaptionText = styled.div`
  margin-top: 0.25rem;
  font-size: 0.72rem;
  line-height: 1.5;
  color: ${INK.muted};
`;

export const DocumentCardAction = styled.button`
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  /* BRAND_GREEN is the pale mint for dark backgrounds; on white the report
     uses the same strong green as its links and citation markers. */
  color: ${SEMAPHORE.green.text};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// ── Comparables ─────────────────────────────────────────────────────────────
// The design's `comps` block (Llívia 77): one row per sale, the façade photo on
// the left, the address and its relation pill on top, four figures in a row
// and the building and note underneath. The subject's own building is tinted.
export const ComparablesList = styled.div`
  margin: 0.25rem 0 1rem;
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  overflow: hidden;
`;

export const ComparableRow = styled.div<{ $highlight?: boolean }>`
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
  padding: 0.875rem 1rem;
  background: ${(p) => (p.$highlight ? TINT_GREEN[50] : WHITE)};

  & + & {
    border-top: 1px solid ${BORDER_SUBTLE};
  }
`;

export const ComparablePhoto = styled.a`
  flex-shrink: 0;
  display: block;
  width: 7rem;
  height: 5.25rem;
  border-radius: 0.5625rem;
  overflow: hidden;
  background: ${WASH};
  border: 1px solid ${BORDER_SUBTLE};

  &:hover {
    opacity: 0.85;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ComparableBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ComparableHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 0.5rem;
`;

export const ComparableLabel = styled.span`
  font-family: ${FONT_FIGTREE};
  font-size: 0.625rem;
  font-weight: 700;
  color: ${INK.faint};
`;

export const ComparableAddress = styled.span`
  font-family: ${FONT_FIGTREE};
  font-size: 0.84rem;
  font-weight: 700;
  color: ${TEXT_VALUE};
  letter-spacing: -0.01em;
`;

export const ComparableRelation = styled.span<{ $highlight?: boolean }>`
  padding: 0.125rem 0.5rem;
  border-radius: 6.25rem;
  font-size: 0.6rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => (p.$highlight ? BORDER_GREEN : TINT_NEUTRAL)};
  color: ${(p) => (p.$highlight ? SEMAPHORE.green.text : INK.muted)};
`;

export const ComparableFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.375rem;
  margin-top: 0.5625rem;
`;

export const ComparableFact = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  white-space: nowrap;
`;

export const ComparableFactLabel = styled.span`
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${INK.label};
`;

export const ComparableFactValue = styled.span<{ $strong?: boolean }>`
  font-family: ${FONT_FIGTREE};
  font-size: 0.8125rem;
  font-weight: ${(p) => (p.$strong ? 800 : 600)};
  color: ${(p) => (p.$strong ? TEXT_VALUE : INK.strong)};
`;

export const ComparableNote = styled.div`
  margin-top: 0.5rem;
  font-size: 0.72rem;
  line-height: 1.45;
  color: ${INK.muted};
  text-wrap: pretty;
`;

export const ComparableBuilding = styled.span`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${INK.label};
`;

// ── Links list (MIKE-322) ───────────────────────────────────────────────────
// The design's `links` block: a bordered list of numbered references, each an
// outbound link to the official source. An item without a url stays as text.
export const LinksList = styled.div`
  margin: 0.25rem 0 0.875rem;
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.8125rem;
  overflow: hidden;
`;

export const LinksRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid ${BORDER_SUBTLE};

  &:last-child {
    border-bottom: none;
  }
`;

export const LinksNumber = styled.span`
  flex-shrink: 0;
  width: 1.625rem;
  text-align: right;
  font-family: ${FONT_FIGTREE};
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${INK.ghost};
`;

export const LinksLabel = styled.span`
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${INK.body};

  a {
    color: ${SEMAPHORE.green.text};
    text-decoration: underline;
    text-decoration-color: ${UNDERLINE_GREEN};
    text-underline-offset: 2px;
  }
`;

// ── PDF exclusion notice (MIKE-322) — shown at the top of a section that the
// export dialog has left out of the client's PDF. Internal view only: in the
// client preview the section is not rendered at all.
export const PdfExclusionNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1.5rem;
  padding: 0.9375rem 1.125rem;
  background: #fdf6e9;
  border: 1px solid ${BORDER_TINT.amber};
  border-radius: 0.8125rem;
  color: ${AMBER_INK};
`;

export const PdfExclusionText = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: #6b5a30;
  text-wrap: pretty;

  strong {
    font-weight: 700;
    color: #5a4a22;
  }
`;

export const PdfExclusionAction = styled.button`
  flex-shrink: 0;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: ${SEMAPHORE.green.fill};
  color: ${WHITE};
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${GREEN_ACCENT};
  }

  &:focus-visible {
    outline: 2px solid ${SEMAPHORE.green.text};
    outline-offset: 2px;
  }
`;

// A pasted drawing (the `svg` element): same frame as a chart, so a hand-made
// figure and a generated one sit at the same weight in the page.
export const SvgFigure = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.875rem;
  padding: 1rem 1.125rem 0.875rem;
  margin: 0.375rem 0 1rem;
  background: ${WHITE};
`;

export const SvgFigureTitle = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${INK.label};
  margin-bottom: 0.875rem;
`;

export const SvgFigureCaption = styled.div`
  margin-top: 0.75rem;
  font-size: 0.688rem;
  color: ${INK.quiet};
  line-height: 1.5;
  text-wrap: pretty;
`;

// Inline image gallery (e.g. §3 cartography). Fixed 3 columns on desktop,
// matching the design's cartoSlots grid — collapses at the same breakpoints
// as KeyDataGrid above for consistency within this file, not auto-fit (a
// fluid grid here would resize each frame's crop unpredictably per image count).
export const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.625rem;
  margin-top: 0.85rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ImageCard = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4375rem;
`;

// Fixed-height, cropped frame around the image itself — the design boxes
// every cartography slot to the same 150px height regardless of the source
// image's own aspect ratio, so a 3-image row always reads as one even grid.
export const ImageFrame = styled.div`
  position: relative;
  height: 9.375rem;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid ${BORDER_SUBTLE};
  background: ${WASH};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const ImageCaption = styled.figcaption`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${INK.soft};
  text-align: center;
  line-height: 1.3;
`;
